/**
 * OptiVehicle Profile — the fact vocabulary, provenance labels, authority and
 * conflict rules, and display helpers. Pure: shared by the browser profile
 * (Create Ticket + Ticket Command Center) and the trusted Edge write path, so
 * what is editable, how a value is validated and which value is authoritative
 * are decided ONE way.
 *
 * SP-01 / SP-05: every fact carries its evidence class —
 *   decoded              from the VIN via an authoritative decoder (NHTSA vPIC)
 *   externally_reported  supplied by a third-party record (future plate→VIN provider)
 *   asserted             entered by a person at the shop
 *   inferred             computed by OptiBay
 * and the classes are never collapsed in presentation.
 *
 * AUTHORITY: a person beats a decoder, a decoder beats an outside report, and
 * an outside report beats an inference. A lower-authority value that disagrees
 * is NOT swapped in and NOT hidden: it is a visible conflict. The order lives
 * ONLY in `authorityOf` below — the database stores what each source said and
 * nothing about who wins — so a future licensed/OE provider can outrank NHTSA
 * for particular facts by changing that one policy, with no history rewritten.
 *
 * BASIC FACTS (year / make / model / engine / GVWR) keep their canonical home
 * in `vehicles` columns, which every existing consumer reads (Dayboard,
 * OptiPlace, lift safety, search, diagnostic matcher). For them the column IS
 * the authoritative value (the compatibility projection); fact rows only say
 * where it came from and whether a decoder disagrees.
 */

import { formatEngineSizeDisplay } from './engineDisplay.ts';

export type SourceClass = 'decoded' | 'externally_reported' | 'asserted' | 'inferred';
export const SOURCE_CLASSES: readonly SourceClass[] = ['decoded', 'externally_reported', 'asserted', 'inferred'];
/** Phase 1 class order. Higher wins. Read it only through authorityOf. */
export const AUTHORITY: Record<SourceClass, number> = { asserted: 4, decoded: 3, externally_reported: 2, inferred: 1 };

export type Section = 'identity' | 'powertrain' | 'configuration';
/** Phase 2: shop-usable groups inside Configuration (not NHTSA's taxonomy). */
export type ConfigGroup = 'chassis' | 'safety' | 'adas_forward' | 'adas_lane' | 'adas_side_rear' | 'adas_lighting';
export const CONFIG_GROUPS: ReadonlyArray<{ group: ConfigGroup; label: string }> = [
  { group: 'adas_forward', label: 'ADAS — Forward' },
  { group: 'adas_lane', label: 'ADAS — Lane' },
  { group: 'adas_side_rear', label: 'ADAS — Side & Rear' },
  { group: 'adas_lighting', label: 'Lighting' },
  { group: 'safety', label: 'Safety Systems' },
  { group: 'chassis', label: 'Chassis & Equipment' },
];
export type FactInput =
  | { kind: 'text'; maxLength: number }
  | { kind: 'integer'; min: number; max: number }
  | { kind: 'decimal'; min: number; max: number }
  | { kind: 'select'; options: readonly string[] };

export interface FactSpec {
  key: string;
  label: string;
  section: Section;
  input: FactInput;
  /** Basic facts live in a `vehicles` column (the compatibility projection). */
  column?: 'year' | 'make' | 'model' | 'engine_size' | 'gvwr_lbs';
  /** Shown in the quick expansion (advisor-useful). */
  quick?: boolean;
  unit?: string;
  /** Phase 2 Configuration group. */
  group?: ConfigGroup;
  /**
   * Equipment fact (three states — present / absent / unknown):
   *   'availability'  NHTSA lookup Standard | Optional | Not Available
   *   'yes_no'        NHTSA lookup Yes | No
   * A person records Equipped | Not equipped.
   */
  equipment?: 'availability' | 'yes_no';
  /** Short ADAS name for the summary line (ACC, FCW, …). */
  abbr?: string;
}

/** What a person may record for an equipment fact. */
export const EQUIPMENT_ASSERTIONS = ['Equipped', 'Not equipped'] as const;
const equipmentInput: FactInput = { kind: 'select', options: EQUIPMENT_ASSERTIONS };

export const DRIVE_TYPES = ['AWD', '4WD', 'FWD', 'RWD', '2WD'] as const;
export const TRANSMISSION_STYLES = ['Automatic', 'Manual', 'Continuously Variable Transmission (CVT)', 'Dual-Clutch Transmission (DCT)', 'Automated Manual Transmission (AMT)'] as const;
export const FUEL_TYPES = ['Gasoline', 'Diesel', 'Electric', 'Flexible Fuel Vehicle (FFV)', 'Compressed Natural Gas (CNG)', 'Liquefied Petroleum Gas (propane or LPG)', 'Hydrogen'] as const;

export const FACT_SPECS: readonly FactSpec[] = [
  // Identity & Build
  { key: 'model_year', label: 'Year', section: 'identity', column: 'year', input: { kind: 'integer', min: 1900, max: 2100 } },
  { key: 'make', label: 'Make', section: 'identity', column: 'make', input: { kind: 'text', maxLength: 60 } },
  { key: 'model', label: 'Model', section: 'identity', column: 'model', input: { kind: 'text', maxLength: 80 } },
  { key: 'trim', label: 'Trim', section: 'identity', quick: true, input: { kind: 'text', maxLength: 80 } },
  { key: 'series', label: 'Series', section: 'identity', input: { kind: 'text', maxLength: 80 } },
  { key: 'body_class', label: 'Body style', section: 'identity', quick: true, input: { kind: 'text', maxLength: 120 } },
  { key: 'doors', label: 'Doors', section: 'identity', input: { kind: 'integer', min: 1, max: 9 } },
  { key: 'vehicle_type', label: 'Vehicle type', section: 'identity', input: { kind: 'text', maxLength: 80 } },
  { key: 'exterior_color', label: 'Exterior color', section: 'identity', input: { kind: 'text', maxLength: 40 } },
  { key: 'manufacturer', label: 'Manufacturer', section: 'identity', input: { kind: 'text', maxLength: 120 } },
  { key: 'plant', label: 'Assembly plant', section: 'identity', input: { kind: 'text', maxLength: 160 } },
  // Powertrain
  { key: 'engine_size', label: 'Engine (shop record)', section: 'powertrain', column: 'engine_size', quick: true, input: { kind: 'text', maxLength: 60 } },
  { key: 'displacement_l', label: 'Displacement', section: 'powertrain', unit: 'L', input: { kind: 'decimal', min: 0.1, max: 20 } },
  { key: 'cylinders', label: 'Cylinders', section: 'powertrain', input: { kind: 'integer', min: 1, max: 16 } },
  { key: 'engine_configuration', label: 'Engine layout', section: 'powertrain', input: { kind: 'text', maxLength: 60 } },
  { key: 'engine_model', label: 'Engine code / family', section: 'powertrain', input: { kind: 'text', maxLength: 60 } },
  { key: 'fuel_type_primary', label: 'Fuel', section: 'powertrain', quick: true, input: { kind: 'select', options: FUEL_TYPES } },
  { key: 'fuel_type_secondary', label: 'Secondary fuel', section: 'powertrain', input: { kind: 'text', maxLength: 80 } },
  { key: 'electrification_level', label: 'Electrification', section: 'powertrain', input: { kind: 'text', maxLength: 80 } },
  { key: 'transmission_style', label: 'Transmission', section: 'powertrain', quick: true, input: { kind: 'select', options: TRANSMISSION_STYLES } },
  { key: 'transmission_speeds', label: 'Transmission speeds', section: 'powertrain', input: { kind: 'integer', min: 1, max: 12 } },
  { key: 'drive_type', label: 'Drivetrain', section: 'powertrain', quick: true, input: { kind: 'select', options: DRIVE_TYPES } },
  { key: 'gvwr_lbs', label: 'GVWR (lift safety)', section: 'powertrain', column: 'gvwr_lbs', quick: true, unit: 'lbs', input: { kind: 'integer', min: 1, max: 100000 } },
  { key: 'gvwr_class', label: 'GVWR class', section: 'powertrain', input: { kind: 'text', maxLength: 80 } },
  // Powertrain — Phase 2
  { key: 'turbo', label: 'Turbocharger', section: 'powertrain', equipment: 'yes_no', input: equipmentInput },
  { key: 'valve_train_design', label: 'Valve train', section: 'powertrain', input: { kind: 'text', maxLength: 80 } },
  { key: 'ev_drive_unit', label: 'EV drive unit', section: 'powertrain', input: { kind: 'text', maxLength: 40 } },
  { key: 'battery_type', label: 'Traction battery type', section: 'powertrain', input: { kind: 'text', maxLength: 80 } },
  // ═══ Configuration (Phase 2) — factory configuration reported for the VIN ═══
  // ADAS — forward
  { key: 'adas_adaptive_cruise_control', label: 'Adaptive cruise control', abbr: 'ACC', section: 'configuration', group: 'adas_forward', equipment: 'availability', input: equipmentInput },
  { key: 'adas_forward_collision_warning', label: 'Forward collision warning', abbr: 'FCW', section: 'configuration', group: 'adas_forward', equipment: 'availability', input: equipmentInput },
  { key: 'adas_crash_imminent_braking', label: 'Automatic emergency braking (crash imminent braking)', abbr: 'AEB', section: 'configuration', group: 'adas_forward', equipment: 'availability', input: equipmentInput },
  { key: 'adas_dynamic_brake_support', label: 'Dynamic brake support', abbr: 'DBS', section: 'configuration', group: 'adas_forward', equipment: 'availability', input: equipmentInput },
  { key: 'adas_pedestrian_aeb', label: 'Pedestrian automatic emergency braking', abbr: 'PAEB', section: 'configuration', group: 'adas_forward', equipment: 'availability', input: equipmentInput },
  // ADAS — lane
  { key: 'adas_lane_departure_warning', label: 'Lane departure warning', abbr: 'LDW', section: 'configuration', group: 'adas_lane', equipment: 'availability', input: equipmentInput },
  { key: 'adas_lane_keeping_assist', label: 'Lane keeping assistance', abbr: 'LKA', section: 'configuration', group: 'adas_lane', equipment: 'availability', input: equipmentInput },
  { key: 'adas_lane_centering_assist', label: 'Lane centering assistance', abbr: 'LCA', section: 'configuration', group: 'adas_lane', equipment: 'availability', input: equipmentInput },
  // ADAS — side & rear
  { key: 'adas_blind_spot_warning', label: 'Blind spot warning', abbr: 'BSW', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  { key: 'adas_blind_spot_intervention', label: 'Blind spot intervention', abbr: 'BSI', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  { key: 'adas_rear_cross_traffic_alert', label: 'Rear cross traffic alert', abbr: 'RCTA', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  { key: 'adas_rear_aeb', label: 'Rear automatic emergency braking', abbr: 'Rear AEB', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  { key: 'adas_parking_assist', label: 'Parking assist', abbr: 'Park assist', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  { key: 'adas_backup_camera', label: 'Backup camera', abbr: 'Backup cam', section: 'configuration', group: 'adas_side_rear', equipment: 'availability', input: equipmentInput },
  // Lighting
  { key: 'adas_adaptive_driving_beam', label: 'Adaptive driving beam', abbr: 'ADB', section: 'configuration', group: 'adas_lighting', equipment: 'availability', input: equipmentInput },
  { key: 'adas_auto_high_beam', label: 'Automatic high beams (semi-automatic beam switching)', abbr: 'Auto high beam', section: 'configuration', group: 'adas_lighting', equipment: 'availability', input: equipmentInput },
  { key: 'lighting_drl', label: 'Daytime running lights', section: 'configuration', group: 'adas_lighting', equipment: 'availability', input: equipmentInput },
  { key: 'lighting_headlamp_source', label: 'Headlamp light source', section: 'configuration', group: 'adas_lighting', input: { kind: 'text', maxLength: 60 } },
  // Safety systems
  { key: 'safety_abs', label: 'Anti-lock brakes (ABS)', section: 'configuration', group: 'safety', equipment: 'availability', input: equipmentInput },
  { key: 'safety_esc', label: 'Electronic stability control', section: 'configuration', group: 'safety', equipment: 'availability', input: equipmentInput },
  { key: 'safety_traction_control', label: 'Traction control', section: 'configuration', group: 'safety', equipment: 'availability', input: equipmentInput },
  { key: 'safety_tpms_type', label: 'Tire pressure monitoring (TPMS)', section: 'configuration', group: 'safety', input: { kind: 'select', options: ['Direct', 'Indirect'] } },
  { key: 'safety_seatbelt_pretensioner', label: 'Seat belt pretensioners', section: 'configuration', group: 'safety', equipment: 'yes_no', input: equipmentInput },
  { key: 'safety_airbags_front', label: 'Front air bags', section: 'configuration', group: 'safety', input: { kind: 'text', maxLength: 120 } },
  { key: 'safety_airbags_side', label: 'Side air bags', section: 'configuration', group: 'safety', input: { kind: 'text', maxLength: 120 } },
  { key: 'safety_airbags_curtain', label: 'Curtain air bags', section: 'configuration', group: 'safety', input: { kind: 'text', maxLength: 120 } },
  { key: 'safety_airbags_knee', label: 'Knee air bags', section: 'configuration', group: 'safety', input: { kind: 'text', maxLength: 120 } },
  { key: 'safety_airbags_seat_cushion', label: 'Seat cushion air bags', section: 'configuration', group: 'safety', input: { kind: 'text', maxLength: 120 } },
  // Chassis & equipment
  { key: 'chassis_axles', label: 'Axles', section: 'configuration', group: 'chassis', input: { kind: 'integer', min: 1, max: 10 } },
  { key: 'chassis_wheelbase_in', label: 'Wheelbase', unit: 'in', section: 'configuration', group: 'chassis', input: { kind: 'decimal', min: 20, max: 400 } },
  { key: 'chassis_wheel_size_front_in', label: 'Front wheel size', unit: 'in', section: 'configuration', group: 'chassis', input: { kind: 'integer', min: 8, max: 30 } },
  { key: 'chassis_wheel_size_rear_in', label: 'Rear wheel size', unit: 'in', section: 'configuration', group: 'chassis', input: { kind: 'integer', min: 8, max: 30 } },
  { key: 'chassis_brake_system_type', label: 'Brake system', section: 'configuration', group: 'chassis', input: { kind: 'text', maxLength: 60 } },
  { key: 'chassis_cab_type', label: 'Cab type', section: 'configuration', group: 'chassis', input: { kind: 'text', maxLength: 60 } },
  { key: 'chassis_bed_type', label: 'Bed type', section: 'configuration', group: 'chassis', input: { kind: 'text', maxLength: 60 } },
  { key: 'equipment_keyless_ignition', label: 'Keyless ignition', section: 'configuration', group: 'chassis', equipment: 'availability', input: equipmentInput },
];

export const FACT_KEYS: readonly string[] = FACT_SPECS.map((s) => s.key);
export const specOf = (key: string): FactSpec | undefined => FACT_SPECS.find((s) => s.key === key);

export interface StoredFact {
  fact_key: string;
  value: string;
  source_class: SourceClass;
  source: string;
  confidence?: string | null;
  recorded_at?: string | null;
}

/** The `vehicles` columns the projection reads. */
export interface VehicleBasics {
  year?: number | null;
  make?: string | null;
  model?: string | null;
  engine_size?: string | null;
  gvwr_lbs?: number | null;
}

export function sourceLabel(sourceClass: SourceClass | 'shop_record', source?: string | null): string {
  if (sourceClass === 'shop_record') return 'Shop record';
  if (sourceClass === 'decoded') return source === 'nhtsa_vpic' ? 'NHTSA decoded' : 'VIN decoded';
  if (sourceClass === 'asserted') return 'Shop entered';
  if (sourceClass === 'externally_reported') return 'External provider';
  return 'OptiBay inferred';
}

const norm = (key: string, v: string | number | null | undefined): string => {
  if (v == null) return '';
  const s = String(v).replace(/\s+/g, ' ').trim().toLowerCase();
  const spec = specOf(key);
  if (spec && (spec.input.kind === 'integer' || spec.input.kind === 'decimal')) {
    const n = Number(s.replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && s !== '' ? String(n) : s;
  }
  return s;
};
/**
 * Equipment facts compare by STATE, and an unknown side (e.g. NHTSA "Optional")
 * never disagrees: a person resolving "optional — verify" to Equipped is a
 * resolution, not a conflict. Only Present vs Not equipped disagree.
 */
export const sameValue = (key: string, a: string | number | null | undefined, b: string | number | null | undefined) => {
  const spec = specOf(key);
  if (spec?.equipment) {
    const sa = equipmentState(spec, a == null ? null : String(a));
    const sb = equipmentState(spec, b == null ? null : String(b));
    return sa === sb || sa === 'unknown' || sb === 'unknown';
  }
  return norm(key, a) === norm(key, b);
};

// ── Phase 2: three-state equipment ────────────────────────────────────────────
export type EquipmentState = 'present' | 'absent' | 'unknown';
/**
 * NEVER turns sparse decoder data into certainty:
 *   Standard | Yes | Equipped            → present
 *   Not Available | No | Not equipped     → absent (an explicit negative only)
 *   Optional (available on the model, not knowable from the VIN), blank,
 *   missing, anything else               → unknown
 */
export function equipmentState(spec: Pick<FactSpec, 'equipment'> | undefined, value: string | null | undefined): EquipmentState {
  if (!spec?.equipment || value == null) return 'unknown';
  const v = value.trim().toLowerCase();
  if (v === 'equipped') return 'present';
  if (v === 'not equipped') return 'absent';
  if (spec.equipment === 'availability') return v === 'standard' ? 'present' : v === 'not available' ? 'absent' : 'unknown';
  return v === 'yes' ? 'present' : v === 'no' ? 'absent' : 'unknown';
}


/**
 * THE authority policy — the single place a winning value is chosen. Phase 1
 * ranks by evidence class only; it receives the fact key and source so a later
 * phase can rank per fact / per source (e.g. an OE build-data provider above
 * NHTSA for trim) without any schema or history change.
 */
export function authorityOf(fact: Pick<StoredFact, 'fact_key' | 'source_class' | 'source'>): number {
  return AUTHORITY[fact.source_class] ?? 0;
}
/** Strongest first; equal authority → the most recent statement. */
const strongestFirst = (a: StoredFact, b: StoredFact) =>
  authorityOf(b) - authorityOf(a) || String(b.recorded_at ?? '').localeCompare(String(a.recorded_at ?? ''));

export interface ResolvedFact {
  key: string;
  spec: FactSpec;
  /** null = Unknown. */
  value: string | null;
  sourceClass: SourceClass | 'shop_record' | null;
  sourceLabel: string | null;
  confidence: string | null;
  /** Current values from OTHER sources that disagree — shown, never silently applied. */
  conflicts: Array<{ value: string; sourceClass: SourceClass; sourceLabel: string }>;
  /** A current shop-entered value exists (for "remove shop value"). */
  hasAssertion: boolean;
}

/**
 * Resolve every fact: the authoritative value, where it came from, and any
 * disagreeing source. `facts` must be the CURRENT (non-superseded) rows.
 */
export function resolveProfile(facts: readonly StoredFact[], basics: VehicleBasics | null): ResolvedFact[] {
  return FACT_SPECS.map((spec) => {
    const mine = facts.filter((f) => f.fact_key === spec.key);
    const asserted = mine.find((f) => f.source_class === 'asserted');
    let value: string | null = null;
    let cls: ResolvedFact['sourceClass'] = null;
    let label: string | null = null;
    let confidence: string | null = null;
    let authoritativeRow: StoredFact | undefined;

    if (spec.column) {
      const col = basics?.[spec.column];
      if (col != null && String(col).trim() !== '') {
        value = String(col);
        // where did the column value come from? a matching person beats a matching decoder
        const match = [...mine].sort(strongestFirst)
          .find((f) => sameValue(spec.key, f.value, value));
        authoritativeRow = match;
        cls = match ? match.source_class : 'shop_record';
        label = match ? sourceLabel(match.source_class, match.source) : sourceLabel('shop_record');
        confidence = match?.confidence ?? null;
      }
    } else if (mine.length) {
      authoritativeRow = [...mine].sort(strongestFirst)[0];
      value = authoritativeRow.value;
      cls = authoritativeRow.source_class;
      label = sourceLabel(authoritativeRow.source_class, authoritativeRow.source);
      confidence = authoritativeRow.confidence ?? null;
    }

    const conflicts = value == null ? [] : mine
      .filter((f) => f !== authoritativeRow && !sameValue(spec.key, f.value, value))
      .map((f) => ({ value: f.value, sourceClass: f.source_class, sourceLabel: sourceLabel(f.source_class, f.source) }));
    // an Unknown column with decoded knowledge is not a conflict — the decoded value is offered as the value
    if (value == null && spec.column && mine.length) {
      const best = [...mine].sort(strongestFirst)[0];
      return { key: spec.key, spec, value: best.value, sourceClass: best.source_class, sourceLabel: sourceLabel(best.source_class, best.source),
        confidence: best.confidence ?? null, conflicts: [], hasAssertion: !!asserted };
    }
    return { key: spec.key, spec, value, sourceClass: cls, sourceLabel: label, confidence, conflicts, hasAssertion: !!asserted };
  });
}

/** Validate a person's value for a fact. Returns the canonical stored text or an error. */
export function validateAssertion(key: string, input: unknown): { ok: true; value: string } | { ok: false; error: string } {
  const spec = specOf(key);
  if (!spec) return { ok: false, error: 'unknown_fact' };
  if (typeof input !== 'string' && typeof input !== 'number') return { ok: false, error: 'value_required' };
  const s = String(input).replace(/\s+/g, ' ').trim();
  if (!s) return { ok: false, error: 'value_required' };
  const i = spec.input;
  if (i.kind === 'text') return s.length <= i.maxLength ? { ok: true, value: s } : { ok: false, error: 'too_long' };
  if (i.kind === 'select') return i.options.includes(s) ? { ok: true, value: s } : { ok: false, error: 'not_an_option' };
  const n = Number(s);
  if (!Number.isFinite(n) || n < i.min || n > i.max) return { ok: false, error: 'out_of_range' };
  if (i.kind === 'integer') return Number.isInteger(n) ? { ok: true, value: String(n) } : { ok: false, error: 'not_an_integer' };
  return { ok: true, value: n.toFixed(1) };
}

// ── display ──────────────────────────────────────────────────────────────────
/**
 * A fact value as shown. The NHTSA GVWR class reads "Class 1D • 5,001–6,000 lb":
 * informational identity only — never the vehicle's confirmed GVWR, never
 * copied into gvwr_lbs, never read by lift safety.
 */
export function formatFactValue(key: string, v: string | null | undefined): string {
  if (!v) return '';
  const spec = specOf(key);
  if (spec?.equipment) {
    const st = equipmentState(spec, v);
    if (st === 'present') return /^standard$/i.test(v.trim()) ? 'Equipped (standard)' : 'Equipped';
    if (st === 'absent') return 'Not equipped';
    return /^optional$/i.test(v.trim()) ? 'Optional on this model — verify on the vehicle' : 'Unknown';
  }
  if (key === 'gvwr_class') {
    const m = /^(Class\s+\w+)\s*:\s*(.+)$/i.exec(v.trim());
    if (m) return `${m[1]} • ${m[2].replace(/\s*-\s*/g, '–')}`;
    return v;
  }
  return displayText(v);
}

/** All-caps decoder text reads as words ("TOURING" → "Touring"); codes and mixed case are kept. */
export function displayText(v: string | null | undefined): string {
  if (!v) return '';
  if (v !== v.toUpperCase() || !/[A-Z]/.test(v)) return v;
  return v.split(' ').map((w) => (/^[A-Z]{4,}[,.]?$/.test(w) ? w[0] + w.slice(1).toLowerCase() : w)).join(' ');
}

const shortBody = (b: string): string => {
  const s = b.toLowerCase();
  if (s.includes('suv') || s.includes('sport utility')) return 'SUV';
  if (s.includes('pickup')) return 'Pickup';
  if (s.includes('minivan')) return 'Minivan';
  if (s.includes('van')) return 'Van';
  for (const k of ['Sedan', 'Coupe', 'Hatchback', 'Convertible', 'Wagon', 'Crossover']) if (s.includes(k.toLowerCase())) return k;
  return displayText(b);
};

const engineShort = (litres: string | null, cyl: string | null, layout: string | null): string | null => {
  if (!litres && !cyl) return null;
  const l = litres ? `${litres}L` : '';
  if (!cyl) return l;
  const lay = (layout ?? '').toLowerCase();
  const prefix = lay.startsWith('v') ? 'V' : lay.startsWith('in-line') || lay.startsWith('inline') ? 'I' : lay.includes('opposed') || lay.includes('boxer') ? 'H' : null;
  return [l, prefix ? `${prefix}${cyl}` : `${cyl} Cyl`].filter(Boolean).join(' ');
};

const transShort = (style: string | null, speeds: string | null): string | null => {
  if (!style && !speeds) return null;
  const s = (style ?? '').toLowerCase();
  const kind = s.startsWith('automatic') ? 'Auto' : s.startsWith('manual') ? 'Manual' : s.includes('cvt') || s.includes('continuously') ? 'CVT'
    : s.includes('dct') || s.includes('dual-clutch') ? 'DCT' : style ? displayText(style) : '';
  if (kind === 'CVT') return 'CVT';
  return [speeds ? `${speeds}-Speed` : '', kind].filter(Boolean).join(' ') || null;
};

/** The compact identity: "2021 Honda Pilot Touring" / "3.5L V6 • AWD • 9-Speed Auto • SUV". Unknown parts are simply absent. */
export function summarize(resolved: readonly ResolvedFact[]): { title: string; specLine: string } {
  const v = (k: string) => resolved.find((r) => r.key === k)?.value ?? null;
  const title = [v('model_year'), displayText(v('make')), displayText(v('model')), displayText(v('trim'))].filter(Boolean).join(' ');
  const engine = engineShort(v('displacement_l'), v('cylinders'), v('engine_configuration')) ?? (v('engine_size') ? formatEngineSizeDisplay(v('engine_size')) : null);
  const parts = [engine, v('drive_type'), transShort(v('transmission_style'), v('transmission_speeds')), v('body_class') ? shortBody(v('body_class')!) : null];
  return { title: title || 'Vehicle', specLine: parts.filter(Boolean).join(' • ') };
}

// ── Phase 2: configuration view-model ────────────────────────────────────────
export interface ConfigItem { fact: ResolvedFact; state: EquipmentState | 'value' | 'not_applicable' | 'unknown' }
/**
 * A configuration item's display state: equipment → its three states; other facts →
 * value | not_applicable (the source said the field does not apply — e.g. no pickup
 * bed on a sedan; distinct from unknown) | unknown (no evidence).
 */
export function configState(fact: ResolvedFact): ConfigItem['state'] {
  if (fact.spec.equipment) return equipmentState(fact.spec, fact.value);
  if (fact.value == null) return 'unknown';
  return /^\s*not applicable\s*$/i.test(fact.value) ? 'not_applicable' : 'value';
}
/** Positively known? (shown by default; absent / unknown wait behind "Show all configuration"). */
export const isPositivelyKnown = (state: ConfigItem['state']) => state === 'present' || state === 'value';
/** The configuration groups, in display order, each with its items. */
export function configurationGroups(resolved: readonly ResolvedFact[]) {
  return CONFIG_GROUPS.map(({ group, label }) => ({
    group, label,
    items: resolved.filter((r) => r.spec.section === 'configuration' && r.spec.group === group).map((fact) => ({ fact, state: configState(fact) })),
  }));
}
/** ADAS reported as present, in display order — the "ADAS reported: ACC · FCW · …" line. */
export function adasPresent(resolved: readonly ResolvedFact[]): string[] {
  return resolved.filter((r) => r.spec.abbr && r.spec.equipment && equipmentState(r.spec, r.value) === 'present').map((r) => r.spec.abbr as string);
}
